/**
 * Centralized API Configuration
 * All API URLs are managed from this single file
 * 
 * Usage:
 * - Import API_URL for all API calls (includes /api suffix)
 * - Import MPGS_CONFIG for Mastercard Payment Gateway URLs
 * - Import COMPANY_CONFIG for company branding information
 */

// Company Branding Configuration
// Change these values to update company name across the entire frontend
export const COMPANY_CONFIGxxx = {
  appname: 'Travel Max',
  name: 'Travel Max',
  fullName: 'Travel Max',
  tagline: 'Bus Ticket Booking',
  taglinelogo: 'Bus Ticket Booking',
  email: 'info@travelmax.lk',
  phone: '(011) 294 5885',
  website: 'www.travelmax.lk'
};

export const COMPANY_CONFIG = {
  appname: 'Nuwantha Enterprises',
  name: 'Nuwantha Enterprises',
  fullName: 'Nuwantha Enterprises',
  tagline: 'Your Transport Partner',
  taglinelogo: 'Your Transport Partner',
  email: 'info@nuwantha.lk',
  phone: '07777 500 22',
  website: 'www.nuwanthaenterprises.com',
  address: '203/B Enderamulla, Wattala'
};

// API Configuration for different environments
// NOTE: These are BACKEND API URLs (with /api), not frontend domains
const API_CONFIG = {
  // Development/Local environment
  // Use this when running frontend locally (npm start)
  development: {
    API_URL: 'http://localhost:5000/api'
  },
  // Production environment
  // Use this when frontend is deployed (even if frontend is on nuwanthaenterprises.com)
  production: {
    API_URL: 'https://nuwantha.vssapps.app/api'
  }
};

// Mastercard Payment Gateway Configuration
export const MPGS_CONFIG = {
  // Merchant ID - should be provided by Mastercard
  merchantId: process.env.REACT_APP_MPGS_MERCHANT_ID || '',
  // Payment gateway URLs
  testUrl: 'https://test-bankofceylon.mtf.gateway.mastercard.com/api/rest/version/100/merchant',
  productionUrl: 'https://bankofceylon.mtf.gateway.mastercard.com/api/rest/version/100/merchant',
  // Hosted checkout URLs - POST endpoint format
  // Mastercard requires POST to /api/page/version/<V>/pay
  testCheckoutUrl: 'https://test-bankofceylon.mtf.gateway.mastercard.com/api/page/version/100/pay',
  productionCheckoutUrl: 'https://bankofceylon.mtf.gateway.mastercard.com/api/page/version/100/pay',
  // Use test or production
  isTestMode: process.env.NODE_ENV !== 'production' || process.env.REACT_APP_MPGS_TEST_MODE === 'true'
};

/**
 * Get API URL (with /api suffix) - Function for runtime evaluation
 * 
 * IMPORTANT: This returns the BACKEND API URL (includes /api), not the frontend domain.
 * Use this function to get the API URL at runtime, especially in production.
 * 
 * Priority:
 * 1. REACT_APP_API_URL environment variable (if set, use as-is) - HIGHEST PRIORITY
 * 2. Check if frontend is running on production domain (use production backend)
 * 3. Use NODE_ENV to determine environment
 * 4. Default to development (localhost)
 * 
 * For Development:
 * - Frontend: http://localhost:3000 (or any local port)
 * - Backend API: http://localhost:5000/api
 * 
 * For Production:
 * - Frontend: https://nuwanthaenterprises.com (or any production domain)
 * - Backend API: https://nuwantha.vssapps.app/api
 */
export const getAPI_URL = () => {
  // Priority 1: Environment variable (highest priority)
  // If REACT_APP_API_URL is set, use it directly
  // Example: REACT_APP_API_URL=http://localhost:5000/api or https://nuwantha.vssapps.app/api
  if (process.env.REACT_APP_API_URL) {
    // Ensure it has /api suffix
    const url = process.env.REACT_APP_API_URL.endsWith('/api') 
      ? process.env.REACT_APP_API_URL 
      : `${process.env.REACT_APP_API_URL}/api`;
    console.log('[API Config] Using REACT_APP_API_URL:', url);
    return url;
  }
  
  // Priority 2: Check if frontend is running on production domain
  // Even if frontend is on nuwanthaenterprises.com, backend is still nuwantha.vssapps.app
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const hostname = window.location.hostname.toLowerCase();
    //const origin = window.location.origin;
    
    // Explicitly check for production domains
    const isProductionDomain = 
      hostname === 'nuwanthaenterprises.com' ||
      hostname === 'www.nuwanthaenterprises.com' ||
      hostname === 'tripimate.com' ||
      hostname === 'www.tripimate.com' ||
      hostname === 'nuwantha.vssapps.app' ||
      hostname === 'www.nuwantha.vssapps.app' ||
      (!hostname.includes('localhost') && 
       !hostname.includes('127.0.0.1') && 
       !hostname.startsWith('192.168.') &&
       !hostname.startsWith('10.') &&
       hostname !== '0.0.0.0');
    
    if (isProductionDomain) {
      console.log('[API Config] Production domain detected:', hostname, '-> Using:', API_CONFIG.production.API_URL);
      return API_CONFIG.production.API_URL;
    } else {
      console.log('[API Config] Development domain detected:', hostname, '-> Using:', API_CONFIG.development.API_URL);
    }
  }
  
  // Priority 3: Use NODE_ENV (fallback if window is not available)
  // When building for production (npm run build), use production backend
  if (process.env.NODE_ENV === 'production') {
    console.log('[API Config] NODE_ENV=production -> Using:', API_CONFIG.production.API_URL);
    return API_CONFIG.production.API_URL;
  }
  
  // Priority 4: Default to development (localhost)
  console.log('[API Config] Defaulting to development:', API_CONFIG.development.API_URL);
  return API_CONFIG.development.API_URL;
};

// Export as a constant for backward compatibility
// NOTE: This is evaluated at module load time. For production runtime detection,
// prefer using getAPI_URL() function which checks window.location at runtime.
// For production builds, ensure NODE_ENV=production or set REACT_APP_API_URL
export const API_URL = getAPI_URL();

// Export individual configs if needed
export const API_CONFIGS = API_CONFIG;

// Export current environment config
export const getCurrentConfig = () => {
  if (process.env.NODE_ENV === 'production') {
    return API_CONFIG.production;
  }
  return API_CONFIG.development;
};

// Debug logging (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration:', {
    API_URL,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL
  });
}